import { ReactNode, useCallback, useMemo, memo } from "react";
import { Box, Flex, useDisclosure } from "@chakra-ui/react";
import { useAuthStore } from "@/store/auth";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Sidebar } from "@/components/layout/Sidebar";
import { useThemeColors } from "@/hooks/useThemeColors";
import Footer from "./Footer";

interface AdminLayoutProps {
  children: ReactNode;
}

function AdminLayoutComponent({ children }: AdminLayoutProps) {
  // Only extract the specific color values we need to prevent re-renders
  const bgColor = useThemeColors().bg;
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Only subscribe to user and logout, not the entire store
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();

  const handleLogout = useCallback(async () => {
    await logout();
    navigate("/login");
  }, [logout, navigate]);

  // Memoize onClose to prevent Sidebar re-renders - use useCallback to ensure stable reference
  const handleSidebarClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Memoize onOpen to prevent Header re-renders
  const handleSidebarOpen = useCallback(() => {
    onOpen();
  }, [onOpen]);

  // Navigation is now driven by user.user_routes inside Sidebar

  return (
    <Box maxH="100vh" bg={bgColor} overflow="hidden">
      <Flex>
        {/* Desktop Sidebar */}
        <Sidebar isOpen={isOpen} onClose={handleSidebarClose} />

        {/* Main Content */}
        <Box flex="1" bg={bgColor} w={{ base: "100%", md: "calc(100% - 64em)" }}>
          {/* Header */}
          <Header
            onOpen={handleSidebarOpen}
            user={user}
            onLogout={handleLogout}
          />

          {/* Page Content */}
          <Box
            bg={bgColor}
            h="calc(100vh - 80px)"
            overflowX="hidden"
            position="relative"
          >
            {children}
            <Footer />
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}

// Memoize AdminLayout to prevent unnecessary re-renders
export const AdminLayout = memo(AdminLayoutComponent);
